import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { insertFmcsaIncidentSchema, type Company, type WorkOrder } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const createIncidentSchema = insertFmcsaIncidentSchema.extend({
  workOrderIds: z.array(z.number()).optional(),
});

type CreateIncidentFormData = z.infer<typeof createIncidentSchema>;

interface CreateFmcsaIncidentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prePopulatedData?: {
    companyId?: number;
    workOrderIds?: number[];
  };
}

export function CreateFmcsaIncidentModal({
  open,
  onOpenChange,
  prePopulatedData,
}: CreateFmcsaIncidentModalProps) {
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<number[]>(prePopulatedData?.workOrderIds || []);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<CreateIncidentFormData>({
    resolver: zodResolver(createIncidentSchema),
    defaultValues: {
      incidentNumber: "",
      status: "Unresolved",
      notes: "",
      companyId: prePopulatedData?.companyId || undefined,
      workOrderIds: prePopulatedData?.workOrderIds || [],
      expedited: false,
    },
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: workOrders } = useQuery<any[]>({
    queryKey: ["/api/work-orders/list", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/work-orders/list?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch work orders");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const selectedCompanyId = form.watch("companyId");
  
  // Filter work orders by selected company
  const filteredWorkOrders = workOrders?.filter(wo => 
    selectedCompanyId ? wo.serviceBureauCompanyId === selectedCompanyId : true
  ) || [];

  const createMutation = useMutation({
    mutationFn: (data: CreateIncidentFormData) =>
      apiRequest("POST", "/api/fmcsa-incidents", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/fmcsa-incidents"] });
      toast({
        title: "Success",
        description: "FMCSA incident created successfully",
      });
      onOpenChange(false);
      form.reset();
      setSelectedWorkOrders(prePopulatedData?.workOrderIds || []);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create FMCSA incident",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CreateIncidentFormData) => {
    createMutation.mutate({
      ...data,
      workOrderIds: selectedWorkOrders,
    });
  };

  const handleWorkOrderToggle = (workOrderId: number) => {
    setSelectedWorkOrders(prev => 
      prev.includes(workOrderId) 
        ? prev.filter(id => id !== workOrderId)
        : [...prev, workOrderId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create FMCSA Incident</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="incidentNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Incident Number *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter FMCSA incident number (e.g., 250708-001027)"
                        required
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Unresolved">Unresolved</SelectItem>
                        <SelectItem value="Resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value ? parseInt(value) : undefined);
                      // Clear selected work orders when company changes
                      setSelectedWorkOrders(prePopulatedData?.workOrderIds || []);
                    }} 
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies?.map((company) => (
                        <SelectItem key={company.id} value={company.id.toString()}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Enter incident details..."
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expedited"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      id="create-expedited"
                    />
                  </FormControl>
                  <FormLabel htmlFor="create-expedited" className="font-medium cursor-pointer">
                    Expedited
                  </FormLabel>
                </FormItem>
              )}
            />

            {/* Work Orders Selection */}
            {filteredWorkOrders.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Link Work Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredWorkOrders.map((workOrder) => (
                      <div key={workOrder.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`work-order-${workOrder.id}`}
                          checked={selectedWorkOrders.includes(workOrder.id)}
                          onCheckedChange={() => handleWorkOrderToggle(workOrder.id)}
                        />
                        <label
                          htmlFor={`work-order-${workOrder.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {workOrder.workOrderNumber} - {workOrder.serviceType}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Incident"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}